import { useState } from "react";
import { createCustomer, updateCustomer } from "../lib/api.ts";

interface CustomerFormModalProps {
  slug: string;
  /** Present → edit mode (prefilled); absent → add mode. */
  initial?: { id: string; name: string; email?: string; phone?: string; notes?: string };
  onClose: () => void;
  onSaved: () => void;
}

export function CustomerFormModal({ slug, initial, onClose, onSaved }: CustomerFormModalProps) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Customer name is required");
      return;
    }
    const t = { name: name.trim(), email: email.trim(), phone: phone.trim(), notes: notes.trim() };
    setSubmitting(true);
    try {
      if (initial?.id) {
        // Edit: send every field (incl. emptied ones) so the user can clear
        // email/phone/notes — the backend normalizes blanks (email/phone → null).
        await updateCustomer(slug, initial.id, t);
      } else {
        // Create: omit empty optionals.
        await createCustomer(slug, {
          name: t.name,
          email: t.email || undefined,
          phone: t.phone || undefined,
          notes: t.notes || undefined,
        });
      }
      setSubmitting(false);
      onSaved();
      onClose();
    } catch (err) {
      const code = (err as { error?: { code?: string } })?.error?.code;
      if (code === "EMAIL_TAKEN") {
        setError("A customer with that email already exists.");
      } else {
        const msg = (err as { error?: { message?: string } })?.error?.message;
        setError(msg ?? "Could not save customer. Please try again.");
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={submitting ? undefined : onClose}
      />

      {/* Sheet / dialog */}
      <form
        onSubmit={handleSubmit}
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--card-bg)",
          border: "1px solid var(--card-border)",
        }}
      >
        {/* Grab handle (mobile) */}
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: "var(--border)" }} />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <h2 style={{ fontSize: "1.05rem", fontWeight: 600, color: "var(--text)" }}>
            {isEdit ? "Edit customer" : "Add customer"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-lg"
            style={{ color: "var(--text-muted)" }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-3">
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jordan Taylor"
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div style={{ minWidth: 0 }}>
              <label className="label">Phone</label>
              <input
                className="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
              />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              className="input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional"
            />
          </div>

          {error && (
            <div className="alert-error" style={{ marginTop: "0.25rem" }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-3" style={{ borderTop: "1px solid var(--border)" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-secondary"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn-primary" style={{ flex: 1 }}>
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Add customer"}
          </button>
        </div>
      </form>
    </div>
  );
}
